package com.dotdrives.repository

import com.dotdrives.client.GiverClient
import com.dotdrives.csv.CsvExistingGiver
import com.dotdrives.model.ActivityEvent
import com.dotdrives.model.Giver
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.NewGiver
import com.dotdrives.model.Note

data class PagedResult<T>(
    val items: List<T>,
    val page: Int,
    val totalCount: Long,
)

enum class TimelineFilter {
    All,
    Gifts,
    Notes,
    DriveMoves,
    Updates,
}

interface GiverRepository {
    suspend fun list(
        orgId: String,
        query: String?,
        stewardUserId: String?,
        giverType: String?,
        tag: String?,
        page: Int,
        pageSize: Int,
    ): Result<PagedResult<GiverSummary>>

    suspend fun get(orgId: String, id: String): Result<Giver>

    suspend fun create(orgId: String, giver: NewGiver): Result<Giver>

    suspend fun createMany(orgId: String, givers: List<NewGiver>): Result<Int>

    suspend fun listForCsvImport(orgId: String): Result<List<CsvExistingGiver>>

    suspend fun update(orgId: String, giver: Giver): Result<Giver>

    suspend fun timeline(
        orgId: String,
        giverId: String,
        filter: TimelineFilter,
        limit: Int,
    ): Result<List<ActivityEvent>>

    suspend fun notes(orgId: String, giverId: String): Result<List<Note>>

    suspend fun addNote(
        orgId: String,
        giverId: String,
        title: String,
        body: String,
    ): Result<Note>
}

class SupabaseGiverRepository(
    private val client: GiverClient = GiverClient(),
    private val includeFinance: Boolean = false,
) : GiverRepository {
    override suspend fun list(
        orgId: String,
        query: String?,
        stewardUserId: String?,
        giverType: String?,
        tag: String?,
        page: Int,
        pageSize: Int,
    ): Result<PagedResult<GiverSummary>> =
        runCatching {
            val result = client.list(
                orgId = orgId,
                query = query,
                stewardUserId = stewardUserId,
                giverType = giverType,
                tag = tag,
                page = page,
                pageSize = pageSize,
                includeFinance = includeFinance,
            )
            PagedResult(
                items = result.items,
                page = page,
                totalCount = result.totalCount,
            )
        }

    override suspend fun get(orgId: String, id: String): Result<Giver> =
        runCatching { client.get(orgId, id) }

    override suspend fun create(orgId: String, giver: NewGiver): Result<Giver> =
        runCatching { client.create(orgId, giver) }

    override suspend fun createMany(orgId: String, givers: List<NewGiver>): Result<Int> =
        runCatching { client.createMany(orgId, givers) }

    override suspend fun listForCsvImport(orgId: String): Result<List<CsvExistingGiver>> =
        runCatching { client.listForCsvImport(orgId) }

    override suspend fun update(orgId: String, giver: Giver): Result<Giver> =
        runCatching { client.update(orgId, giver) }

    override suspend fun timeline(
        orgId: String,
        giverId: String,
        filter: TimelineFilter,
        limit: Int,
    ): Result<List<ActivityEvent>> =
        if (filter in UNSUPPORTED_TIMELINE_FILTERS) {
            Result.success(emptyList())
        } else {
            runCatching {
                client.timeline(
                    orgId = orgId,
                    giverId = giverId,
                    verb = if (filter == TimelineFilter.Notes) NOTE_ADDED_VERB else null,
                    limit = limit,
                )
            }
        }

    override suspend fun notes(orgId: String, giverId: String): Result<List<Note>> =
        runCatching { client.notes(orgId, giverId) }

    override suspend fun addNote(
        orgId: String,
        giverId: String,
        title: String,
        body: String,
    ): Result<Note> =
        runCatching { client.addNote(orgId, giverId, title, body) }
}

private val UNSUPPORTED_TIMELINE_FILTERS = setOf(
    TimelineFilter.Gifts,
    TimelineFilter.DriveMoves,
    TimelineFilter.Updates,
)
private const val NOTE_ADDED_VERB = "note.added"
